import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faLock } from '@fortawesome/free-solid-svg-icons';
import { guestApi, getGuestSession, setGuestSession } from '../utils/portalApi';
import '../styles/portal.css';
import '../styles/guest.css';

/**
 * Guest sign-in (guest role): an email, then a six-digit code sent to it - or,
 * for a guest who has set one or been given one, their password.
 *
 * The code stays the way in for everybody, so "forgot my password" is just
 * signing in with a code and setting a new one from the profile.
 *
 * Staff sign in at /login. Nothing here touches the employee session.
 */
const GuestLogin = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [method, setMethod] = useState('code');
    const [step, setStep] = useState('email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [info, setInfo] = useState(location.state?.message || '');

    useEffect(() => {
        if (getGuestSession()) navigate('/guest', { replace: true });
    }, [navigate]);

    const requestCode = async (e) => {
        e?.preventDefault();
        setBusy(true);
        setError('');
        try {
            const { data } = await guestApi.post('/login/request', { email: email.trim() });
            setInfo(data.message);
            setStep('code');
        } catch (err) {
            setError(err.response?.data?.message || 'Could not send a code. Try again shortly.');
        } finally {
            setBusy(false);
        }
    };

    const signIn = async (path, body) => {
        setBusy(true);
        setError('');
        try {
            const { data } = await guestApi.post(path, body);
            setGuestSession(data.token);
            const from = location.state?.from;
            navigate(from && from.startsWith('/guest/') ? from : '/guest', { replace: true });
        } catch (err) {
            setError(err.response?.data?.message || 'Could not sign you in.');
        } finally {
            setBusy(false);
        }
    };

    const verify = (e) => {
        e.preventDefault();
        signIn('/login/verify', { email: email.trim(), code });
    };

    const withPassword = (e) => {
        e.preventDefault();
        signIn('/login/password', { email: email.trim(), password });
    };

    const switchTo = (next) => {
        setMethod(next);
        setStep('email');
        setCode('');
        setPassword('');
        setError('');
    };

    return (
        <div className="portal-shell">
            <div className="portal-card">
                <div className="portal-brand">GTS</div>
                <p className="portal-eyebrow">Guest access</p>
                <h2>Sign in as a guest</h2>

                {step === 'email' && (
                    <div className="guest-method" role="group" aria-label="How to sign in">
                        <button type="button" aria-pressed={method === 'code'} onClick={() => switchTo('code')}>Email me a code</button>
                        <button type="button" aria-pressed={method === 'password'} onClick={() => switchTo('password')}>Use a password</button>
                    </div>
                )}

                {method === 'password' ? (
                    <form onSubmit={withPassword}>
                        {info && <p className="guest-muted">{info}</p>}
                        <input className="ext-input guest-login-input" type="email" autoFocus required autoComplete="username"
                            placeholder="you@company.com" value={email}
                            onChange={(e) => { setEmail(e.target.value); setError(''); }} />
                        <input className="ext-input guest-login-input" type="password" required autoComplete="current-password"
                            placeholder="Password" value={password}
                            onChange={(e) => { setPassword(e.target.value); setError(''); }} />
                        {error && <p className="portal-error">{error}</p>}
                        <button className="portal-primary" disabled={busy || !email.trim() || !password}>
                            {busy ? 'Signing in…' : <>Sign in <FontAwesomeIcon icon={faArrowRight} /></>}
                        </button>
                        <p className="guest-links">
                            <button type="button" className="guest-link" onClick={() => switchTo('code')}>
                                Forgot your password? Sign in with a code
                            </button>
                        </p>
                    </form>
                ) : step === 'email' ? (
                    <form onSubmit={requestCode}>
                        <p className="portal-intro">
                            Use the email address your invitation was sent to. We will email you a sign-in code.
                        </p>
                        {info && <p className="guest-muted">{info}</p>}
                        <input className="ext-input guest-login-input" type="email" autoFocus required
                            placeholder="you@company.com" value={email}
                            onChange={(e) => { setEmail(e.target.value); setError(''); }} />
                        {error && <p className="portal-error">{error}</p>}
                        <button className="portal-primary" disabled={busy || !email.trim()}>
                            {busy ? 'Sending…' : <>Send me a code <FontAwesomeIcon icon={faArrowRight} /></>}
                        </button>
                    </form>
                ) : (
                    <form onSubmit={verify}>
                        <p className="portal-intro">{info}</p>
                        <label className="portal-label">Enter the six-digit code sent to {email}</label>
                        <input className="portal-code" value={code} inputMode="numeric" maxLength={6} autoFocus
                            placeholder="000000"
                            onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }} />
                        {error && <p className="portal-error">{error}</p>}
                        <button className="portal-primary" disabled={busy || code.length !== 6}>
                            {busy ? 'Signing in…' : <>Sign in <FontAwesomeIcon icon={faArrowRight} /></>}
                        </button>
                        <p className="guest-links">
                            <button type="button" className="guest-link" disabled={busy} onClick={requestCode}>Send a new code</button>
                            <button type="button" className="guest-link" onClick={() => { setStep('email'); setCode(''); setError(''); }}>Use a different email</button>
                        </p>
                    </form>
                )}

                <p className="portal-foot">
                    <FontAwesomeIcon icon={faLock} />
                    You will only see the conversations and meetings you were invited to.
                </p>
            </div>
        </div>
    );
};

export default GuestLogin;
